import { getSpeakerNameFromLabel } from '@src/utility/speakerUtils';
import { runInAction } from 'mobx';
import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

import { useOutsideAlerter } from '../../../components/transcript/useClickOutside';
import ChatGPTService from '../../../services/ChatGPTService';
import { useDatoClient } from '../../../stores-v2/StoreContext';
import { useVideoCreatorStore } from '../../../stores-v2/VideoCreatorStoreContext';
import {
  formatTimestamp,
  type FrameAnalysisRequest,
  type FrameAnalysisResult,
  getDenseFrameNumbersForSpeaker,
  getFrameNumbersForSpeakerAnalysis,
} from '../../utils/frameExtraction';

import type { Segment } from '../../models/Segment';
import type { DatoSpeaker } from '../../services/interfaces/ISpeakerService';
import type { TranscriptEditorStore } from '../../TranscriptEditorStore';

interface Speaker {
  speakerLabel: string;
  datoSpeakerId: string;
  name: string;
  title?: string;
  voice?: string;
  bio?: string;
  color: string;
}

interface IdentifiedSpeaker {
  speakerLabel: string;
  name: string;
  title: string;
}

interface SpeakerIdentificationModalProps {
  isOpen: boolean;
  onClose: () => void;
  editorStore: TranscriptEditorStore;
  anonymousSpeakers: Speaker[];
}

const SpeakerIdentificationModal: React.FC<SpeakerIdentificationModalProps> = ({
  isOpen,
  onClose,
  editorStore,
  anonymousSpeakers,
}) => {
  const [isIdentifying, setIsIdentifying] = useState(false);
  const [identifiedSpeakers, setIdentifiedSpeakers] = useState<
    IdentifiedSpeaker[]
  >([]);
  const [selectedMappings, setSelectedMappings] = useState<
    Record<string, IdentifiedSpeaker | Speaker | null>
  >({});
  const [error, setError] = useState<string | null>(null);
  const [isApplying, setIsApplying] = useState(false);
  const [applyingProgress, setApplyingProgress] = useState<string>('');
  const [existingSpeakers, setExistingSpeakers] = useState<Speaker[]>([]);
  const [isAnalyzingFrames, setIsAnalyzingFrames] = useState(false);
  const [frameAnalysisResults, setFrameAnalysisResults] = useState<
    FrameAnalysisResult[]
  >([]);
  const [showFramePreview, setShowFramePreview] = useState(false);
  const [identificationProgress, setIdentificationProgress] =
    useState<string>('');

  // Get videoCreator and create ChatGPTService instance
  const videoCreator = useVideoCreatorStore();
  const datoClientStore = useDatoClient();
  const chatGPTServiceRef = useRef<ChatGPTService | null>(null);

  // Initialize ChatGPTService when component mounts
  useEffect(() => {
    if (videoCreator && datoClientStore?.aiPromptRepository) {
      chatGPTServiceRef.current = new ChatGPTService(
        videoCreator,
        datoClientStore.aiPromptRepository,
      );
    }
  }, [videoCreator, datoClientStore]);

  // Initialize selected mappings and load existing speakers when modal opens
  useEffect(() => {
    if (isOpen) {
      const initialMappings: Record<
        string,
        IdentifiedSpeaker | Speaker | null
      > = {};
      anonymousSpeakers.forEach(speaker => {
        initialMappings[speaker.speakerLabel] = null;
      });
      setSelectedMappings(initialMappings);

      // Load existing speakers from speaker service
      const speakerService = editorStore.editor?.getSpeakerService();
      if (speakerService) {
        const speakers = speakerService.getAllSpeakers(
          editorStore.editor?.getContainer(),
          editorStore,
        );
        // Get all story and organization speakers (not anonymous ones)
        const allExisting = [...speakers.story, ...speakers.album];
        setExistingSpeakers(allExisting);
      }
    }
  }, [isOpen, anonymousSpeakers, editorStore]);

  // Handle escape key
  useEffect(() => {
    if (!isOpen) return;

    const handleEscapeKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscapeKey);
    return () => {
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  // Function to find potential matches for an identified speaker
  const findPotentialMatches = (
    identifiedSpeaker: IdentifiedSpeaker,
  ): Speaker[] => {
    const nameLower = identifiedSpeaker.name.toLowerCase();

    return existingSpeakers.filter(existing => {
      const existingNameLower = existing.name.toLowerCase();

      // Check for exact name match
      if (existingNameLower === nameLower) return true;

      return false;
    });
  };

  const handleIdentifySpeakers = async () => {
    if (!chatGPTServiceRef.current) {
      setError('ChatGPT service not available');
      return;
    }

    setIsIdentifying(true);
    setError(null);

    try {
      // Get transcript segments first (needed for all passes)
      const container = editorStore.container;
      const segments =
        container && container.segments
          ? container.segments.map(seg => ({
              speaker: seg.speakerLabel,
              start: seg.startTime,
              end: seg.endTime,
              text: seg.paragraphs
                ? seg.paragraphs
                    .map(p =>
                      p.elements
                        ? p.elements
                            .filter(e => e.type === 'text')
                            .map(e => e.value)
                            .join(' ')
                        : '',
                    )
                    .join(' ')
                : '',
            }))
          : [];

      // Get all speakers from the editor store to pass to the AI
      const speakerService = editorStore.editor?.getSpeakerService();
      let allSpeakers: Speaker[] = [];

      if (speakerService) {
        const speakers = speakerService.getAllSpeakers(
          editorStore.editor?.getContainer(),
          editorStore,
        );
        // Combine all speaker categories
        allSpeakers = [
          ...speakers.mapped,
          ...speakers.unmapped,
          ...speakers.story,
          ...speakers.album,
        ];
      }

      // PASS 1: Try to identify speakers from transcript only
      console.log('Pass 1: Identifying speakers from transcript...');
      setIdentificationProgress(
        `Pass 1: Analyzing transcript for ${anonymousSpeakers.length} speakers (using chunked processing for long transcripts)...`,
      );
      const identifiedFromTranscript =
        await chatGPTServiceRef.current.identifySpeakers({
          selectedSpeakers: allSpeakers,
        });

      // Check which speakers were not identified
      const unidentifiedSpeakers = anonymousSpeakers.filter(
        speaker =>
          !identifiedFromTranscript.find(
            id => id.speakerLabel === speaker.speakerLabel,
          ),
      );

      console.log(
        `Pass 1 complete: ${identifiedFromTranscript.length} identified, ${unidentifiedSpeakers.length} unidentified`,
      );
      setIdentificationProgress(
        `Pass 1 complete: ${identifiedFromTranscript.length}/${anonymousSpeakers.length} speakers identified`,
      );

      let frameAnalysisResultsLocal: FrameAnalysisResult[] = [];
      let finalIdentified = [...identifiedFromTranscript];

      // PASS 2: If we have unidentified speakers, analyze frames for them
      if (unidentifiedSpeakers.length > 0 && editorStore.storyId) {
        setIsAnalyzingFrames(true);
        setIdentificationProgress(
          `Pass 2: Analyzing video frames for ${unidentifiedSpeakers.length} unidentified speakers...`,
        );
        console.log('Pass 2: Analyzing frames for unidentified speakers...');

        // Filter segments to only unidentified speakers
        const unidentifiedSegments = segments.filter(seg =>
          unidentifiedSpeakers.some(
            speaker => speaker.speakerLabel === seg.speaker,
          ),
        );

        const speakerFrames =
          getFrameNumbersForSpeakerAnalysis(unidentifiedSegments);

        if (speakerFrames.length > 0) {
          try {
            const arborApiUrl =
              process.env.REACT_APP_API_URL || window.location.origin;
            const frameResponse = await fetch(
              `${arborApiUrl}/api/video/analyze-speaker-frames`,
              {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                  storyId: editorStore.storyId,
                  videoId: editorStore.storyId,
                  speakerFrames,
                }),
              },
            );

            if (frameResponse.ok) {
              const frameData = await frameResponse.json();
              frameAnalysisResultsLocal = frameData.results || [];
              console.log(
                'Pass 2 frame analysis results:',
                frameAnalysisResultsLocal,
              );
              setFrameAnalysisResults(frameAnalysisResultsLocal);

              // Run identification again with frame context for unidentified speakers
              if (frameAnalysisResultsLocal.some(r => r.hasLowerThird)) {
                const identifiedFromFrames =
                  await chatGPTServiceRef.current.identifySpeakersWithFrameAnalysis(
                    {
                      selectedSpeakers: allSpeakers,
                      frameAnalysisResults: frameAnalysisResultsLocal,
                    },
                  );

                // Merge results, keeping transcript results and adding new frame results
                identifiedFromFrames.forEach(frameId => {
                  if (
                    !finalIdentified.find(
                      id => id.speakerLabel === frameId.speakerLabel,
                    )
                  ) {
                    finalIdentified.push(frameId);
                  }
                });

                console.log(
                  `Pass 2 complete: ${identifiedFromFrames.length} additional speakers identified`,
                );
                setIdentificationProgress(
                  `Pass 2 complete: ${finalIdentified.length}/${anonymousSpeakers.length} speakers identified`,
                );
              }
            }
          } catch (frameError) {
            console.error('Frame analysis failed:', frameError);
          }
        }

        // Check if we still have unidentified speakers
        const stillUnidentified = unidentifiedSpeakers.filter(
          speaker =>
            !finalIdentified.find(
              id => id.speakerLabel === speaker.speakerLabel,
            ),
        );

        // PASS 3: Dense frame analysis for remaining unidentified speakers
        if (stillUnidentified.length > 0) {
          setIdentificationProgress(
            `Pass 3: Dense frame analysis for ${stillUnidentified.length} remaining speakers...`,
          );
          console.log(
            `Pass 3: Dense frame analysis for ${stillUnidentified.length} remaining speakers...`,
          );

          // Get dense frames for each unidentified speaker
          const denseFrames: FrameAnalysisRequest[] = [];
          stillUnidentified.forEach(speaker => {
            const speakerDenseFrames = getDenseFrameNumbersForSpeaker(
              segments,
              speaker.speakerLabel,
              5, // Every 5 seconds
              10, // Max 10 frames per speaker
            );
            denseFrames.push(...speakerDenseFrames);
          });

          if (denseFrames.length > 0) {
            try {
              const arborApiUrl =
                process.env.REACT_APP_API_URL || window.location.origin;
              const denseFrameResponse = await fetch(
                `${arborApiUrl}/api/video/analyze-speaker-frames`,
                {
                  method: 'POST',
                  headers: {
                    'Content-Type': 'application/json',
                  },
                  body: JSON.stringify({
                    storyId: editorStore.storyId,
                    videoId: editorStore.storyId,
                    speakerFrames: denseFrames,
                  }),
                },
              );

              if (denseFrameResponse.ok) {
                const denseFrameData = await denseFrameResponse.json();
                const denseResults = denseFrameData.results || [];
                console.log(
                  'Pass 3 dense frame analysis results:',
                  denseResults,
                );

                // Add to frame analysis results
                frameAnalysisResultsLocal = [
                  ...frameAnalysisResultsLocal,
                  ...denseResults,
                ];
                setFrameAnalysisResults(frameAnalysisResultsLocal);

                // Run identification again with all frame context
                if (denseResults.some(r => r.hasLowerThird)) {
                  const identifiedFromDenseFrames =
                    await chatGPTServiceRef.current.identifySpeakersWithFrameAnalysis(
                      {
                        selectedSpeakers: allSpeakers,
                        frameAnalysisResults: frameAnalysisResultsLocal,
                      },
                    );

                  // Merge results
                  identifiedFromDenseFrames.forEach(frameId => {
                    if (
                      !finalIdentified.find(
                        id => id.speakerLabel === frameId.speakerLabel,
                      )
                    ) {
                      finalIdentified.push(frameId);
                    }
                  });

                  console.log(
                    `Pass 3 complete: ${identifiedFromDenseFrames.length} additional speakers identified`,
                  );
                  setIdentificationProgress(
                    `Pass 3 complete: ${finalIdentified.length}/${anonymousSpeakers.length} speakers identified`,
                  );
                }
              }
            } catch (denseFrameError) {
              console.error('Dense frame analysis failed:', denseFrameError);
            }
          } else {
            setIdentificationProgress(
              `Pass 3 complete: ${finalIdentified.length}/${anonymousSpeakers.length} speakers identified (no frames to analyze)`,
            );
          }
        }

        setIsAnalyzingFrames(false);
      }

      // Check if we STILL have unidentified speakers after Pass 3
      const finalUnidentified = anonymousSpeakers.filter(
        speaker =>
          !finalIdentified.find(id => id.speakerLabel === speaker.speakerLabel),
      );

      // PASS 4: Ultra-dense frame analysis (every 2 seconds) with batching
      if (finalUnidentified.length > 0 && editorStore.storyId) {
        setIsAnalyzingFrames(true);
        setIdentificationProgress(
          `Pass 4: Ultra-dense analysis for ${finalUnidentified.length} remaining speakers...`,
        );
        console.log(
          `Pass 4: Ultra-dense frame analysis for ${finalUnidentified.length} remaining speakers...`,
        );

        // Process each unidentified speaker in batches
        for (const speaker of finalUnidentified) {
          // Get all frames for this speaker (every 2 seconds)
          const ultraDenseFrames = getDenseFrameNumbersForSpeaker(
            segments,
            speaker.speakerLabel,
            2, // Every 2 seconds
            30, // Max 30 frames (1 minute of footage)
          );

          if (ultraDenseFrames.length === 0) continue;

          // Process in batches of 5 frames
          const batchSize = 5;
          let speakerIdentified = false;

          for (
            let i = 0;
            i < ultraDenseFrames.length && !speakerIdentified;
            i += batchSize
          ) {
            const batch = ultraDenseFrames.slice(
              i,
              Math.min(i + batchSize, ultraDenseFrames.length),
            );

            setIdentificationProgress(
              `Pass 4: Analyzing frames ${i + 1}-${Math.min(i + batchSize, ultraDenseFrames.length)} of ${ultraDenseFrames.length} for ${speaker.speakerLabel}...`,
            );

            try {
              const arborApiUrl =
                process.env.REACT_APP_API_URL || window.location.origin;
              const batchResponse = await fetch(
                `${arborApiUrl}/api/video/analyze-speaker-frames`,
                {
                  method: 'POST',
                  headers: {
                    'Content-Type': 'application/json',
                  },
                  body: JSON.stringify({
                    storyId: editorStore.storyId,
                    videoId: editorStore.storyId,
                    speakerFrames: batch,
                  }),
                },
              );

              if (batchResponse.ok) {
                const batchData = await batchResponse.json();
                const batchResults = batchData.results || [];

                // Check if any frames have lower thirds
                const framesWithInfo = batchResults.filter(
                  r => r.hasLowerThird,
                );

                if (framesWithInfo.length > 0) {
                  console.log(
                    `Pass 4: Found ${framesWithInfo.length} frames with info for ${speaker.speakerLabel}`,
                  );

                  // Add to frame analysis results
                  frameAnalysisResultsLocal = [
                    ...frameAnalysisResultsLocal,
                    ...batchResults,
                  ];
                  setFrameAnalysisResults(frameAnalysisResultsLocal);

                  // Try to identify with the new frames
                  const identifiedFromBatch =
                    await chatGPTServiceRef.current.identifySpeakersWithFrameAnalysis(
                      {
                        selectedSpeakers: allSpeakers,
                        frameAnalysisResults: frameAnalysisResultsLocal,
                      },
                    );

                  // Check if this speaker was identified
                  const newlyIdentified = identifiedFromBatch.find(
                    id => id.speakerLabel === speaker.speakerLabel,
                  );
                  if (newlyIdentified) {
                    finalIdentified.push(newlyIdentified);
                    speakerIdentified = true;
                    console.log(
                      `Pass 4: Successfully identified ${speaker.speakerLabel} as ${newlyIdentified.name}`,
                    );
                    setIdentificationProgress(
                      `Pass 4: Identified ${speaker.speakerLabel} as ${newlyIdentified.name}. ${finalIdentified.length}/${anonymousSpeakers.length} total identified.`,
                    );
                  }
                }
              }
            } catch (batchError) {
              console.error(
                `Pass 4 batch analysis failed for ${speaker.speakerLabel}:`,
                batchError,
              );
            }
          }

          if (!speakerIdentified) {
            console.log(
              `Pass 4: Could not identify ${speaker.speakerLabel} after analyzing ${ultraDenseFrames.length} frames`,
            );
          }
        }

        setIsAnalyzingFrames(false);
      }

      const identified = finalIdentified;

      // Final summary
      const unidentifiedCount = anonymousSpeakers.length - identified.length;
      const summaryMessage =
        unidentifiedCount > 0
          ? `Analysis complete: ${identified.length}/${anonymousSpeakers.length} speakers identified. ${unidentifiedCount} speaker${unidentifiedCount > 1 ? 's' : ''} could not be identified.`
          : `Analysis complete: All ${identified.length} speakers successfully identified!`;

      setIdentificationProgress(summaryMessage);
      console.log(
        `Final results: ${identified.length} speakers identified out of ${anonymousSpeakers.length} total`,
      );

      // Check if we got any results
      if (!identified || identified.length === 0) {
        setError(
          'No speakers could be identified from the transcript. Please ensure speakers introduce themselves or are mentioned by name.',
        );
        setIdentifiedSpeakers([]);
        return;
      }

      setIdentifiedSpeakers(identified);
      // Auto-map identified speakers to anonymous speakers with match checking
      const newMappings: Record<string, IdentifiedSpeaker | Speaker | null> =
        {};
      anonymousSpeakers.forEach(speaker => {
        const identifiedMatch = identified.find(
          id => id.speakerLabel === speaker.speakerLabel,
        );

        if (identifiedMatch) {
          // Check if we have an existing speaker that matches
          const potentialMatches = findPotentialMatches(identifiedMatch);
          if (potentialMatches.length > 0) {
            // Use the first match as default (could be improved with better scoring)
            newMappings[speaker.speakerLabel] = potentialMatches[0];
          } else {
            // No existing match, use the identified speaker
            newMappings[speaker.speakerLabel] = identifiedMatch;
          }
        } else {
          newMappings[speaker.speakerLabel] = null;
        }
      });
      setSelectedMappings(newMappings);
    } catch (err) {
      console.error('Error identifying speakers:', err);
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';
      setError(
        `Failed to identify speakers: ${errorMessage}. Please try again or contact support if the issue persists.`,
      );
    } finally {
      setIsIdentifying(false);
    }
  };

  const handleMappingChange = (
    speakerLabel: string,
    mapping: IdentifiedSpeaker | Speaker | null,
  ) => {
    setSelectedMappings(prev => ({
      ...prev,
      [speakerLabel]: mapping,
    }));
  };

  const handleApplyMappings = async () => {
    setIsApplying(true);
    setError(null);
    setApplyingProgress('Preparing speaker mappings...');

    try {
      const speakerService = editorStore.editor?.getSpeakerService();
      if (!speakerService) {
        throw new Error('Speaker service not available');
      }

      // Get all existing speakers to check for duplicates
      const allExistingSpeakers = [
        ...editorStore.storySpeakers,
        ...editorStore.albumSpeakers,
      ];

      // Group new speakers by their unique key to identify duplicates
      const newSpeakersToCreate = new Map<
        string,
        {
          identifiedSpeaker: IdentifiedSpeaker;
          speakerLabels: string[];
        }
      >();

      // Track existing speaker mappings separately
      const existingSpeakerMappings: Record<string, string> = {};

      // Group speakers that need consolidation (including existing speakers)
      const speakersToConsolidate = new Map<
        string,
        {
          targetSpeaker: IdentifiedSpeaker | Speaker;
          speakerLabels: string[];
          targetDatoId?: string;
        }
      >();

      // First pass: group all speakers by their identified name
      const identifiedNames = new Map<string, string[]>(); // name -> speaker labels

      for (const [speakerLabel, mapping] of Object.entries(selectedMappings)) {
        if (mapping) {
          let name: string;
          let title: string = '';

          if ('datoSpeakerId' in mapping) {
            // Existing speaker
            name = mapping.name.toLowerCase().trim();
            title = (mapping.title || '').toLowerCase().trim();
          } else {
            // New identified speaker
            name = mapping.name.toLowerCase().trim();
            title = (mapping.title || '').toLowerCase().trim();
          }

          const key = `${name}_${title}`;
          if (!identifiedNames.has(key)) {
            identifiedNames.set(key, []);
          }
          identifiedNames.get(key)!.push(speakerLabel);
        }
      }

      // Second pass: process each group
      for (const [nameKey, speakerLabels] of identifiedNames) {
        if (speakerLabels.length > 1) {
          // Multiple speakers identified as the same person - need consolidation
          // Sort to get primary speaker
          const sortedLabels = [...speakerLabels].sort((a, b) => {
            const aNum = parseInt(a.replace('spk_', ''));
            const bNum = parseInt(b.replace('spk_', ''));
            return aNum - bNum;
          });

          const primaryLabel = sortedLabels[0];
          const primaryMapping = selectedMappings[primaryLabel];

          if (primaryMapping) {
            speakersToConsolidate.set(nameKey, {
              targetSpeaker: primaryMapping,
              speakerLabels: sortedLabels,
              targetDatoId:
                'datoSpeakerId' in primaryMapping
                  ? primaryMapping.datoSpeakerId
                  : undefined,
            });
          }
        } else {
          // Single speaker - check if it's new or existing
          const speakerLabel = speakerLabels[0];
          const mapping = selectedMappings[speakerLabel];

          if (mapping && 'datoSpeakerId' in mapping) {
            // Existing speaker - just keep the mapping
            existingSpeakerMappings[speakerLabel] = mapping.datoSpeakerId;
          } else if (mapping) {
            // New speaker to create
            newSpeakersToCreate.set(nameKey, {
              identifiedSpeaker: mapping as IdentifiedSpeaker,
              speakerLabels: [speakerLabel],
            });
          }
        }
      }

      // Process speaker consolidation first

      // Import the command class dynamically to avoid circular dependencies
      const { AssignSpeakerToSegmentCommand } = await import(
        '../../commands/AssignSpeakerToSegmentCommand'
      );

      // Handle consolidation for speakers with multiple labels
      for (const [
        nameKey,
        { targetSpeaker, speakerLabels, targetDatoId },
      ] of speakersToConsolidate) {
        const primaryLabel = speakerLabels[0];
        const secondaryLabels = speakerLabels.slice(1);

        if (secondaryLabels.length > 0) {
          setApplyingProgress(
            `Consolidating ${speakerLabels.length} instances of ${targetSpeaker.name}...`,
          );

          // Get the container and editor
          const container = editorStore.container;
          const editor = editorStore.editor;
          const commandManager = editor?.commandManager;

          if (container && editor && commandManager) {
            // For each secondary speaker, reassign all their segments to the primary speaker
            for (const secondaryLabel of secondaryLabels) {
              // Find all segments with this speaker label
              const segmentsToReassign = container.segments.filter(
                (seg: Segment) => seg.speakerLabel === secondaryLabel,
              );

              if (segmentsToReassign.length > 0) {
                // Create and execute a command to reassign each segment
                for (let i = 0; i < segmentsToReassign.length; i++) {
                  const segment = segmentsToReassign[i];
                  const command = new AssignSpeakerToSegmentCommand(
                    editor,
                    segment,
                    primaryLabel,
                    null, // No dato speaker ID yet
                    editorStore.currentUserId || 'system',
                  );

                  // Execute the command through the command manager
                  await commandManager.execute(command);
                }
              }
            }
          }
        }

        // Map to existing speaker if applicable
        if (targetDatoId) {
          existingSpeakerMappings[primaryLabel] = targetDatoId;
        } else if (!('datoSpeakerId' in targetSpeaker)) {
          // It's a new speaker that needs to be created
          newSpeakersToCreate.set(nameKey, {
            identifiedSpeaker: targetSpeaker as IdentifiedSpeaker,
            speakerLabels: [primaryLabel],
          });
        }
      }

      // Process each unique speaker group
      let speakerCount = 0;
      const totalNewSpeakers = newSpeakersToCreate.size;
      const finalMappings: Record<string, string> = {};

      for (const [
        ,
        { identifiedSpeaker, speakerLabels },
      ] of newSpeakersToCreate) {
        speakerCount++;

        // Now handle the speaker creation/mapping for the primary speaker only
        const primarySpeakerLabel = speakerLabels[0]; // Already processed consolidation above
        setApplyingProgress(
          `Creating speaker ${speakerCount} of ${totalNewSpeakers}: ${identifiedSpeaker.name}...`,
        );

        // Check if a speaker with this name/title already exists in the database
        const existingSpeaker = allExistingSpeakers.find(
          s =>
            s.name.toLowerCase().trim() ===
              identifiedSpeaker.name.toLowerCase().trim() &&
            (s.title || '').toLowerCase().trim() ===
              (identifiedSpeaker.title || '').toLowerCase().trim(),
        );

        let speaker: DatoSpeaker;

        if (existingSpeaker) {
          // Use the existing speaker
          speaker = existingSpeaker;
        } else {
          // Create the speaker (without assignment)
          speaker = await speakerService.createSpeaker(
            identifiedSpeaker.name,
            identifiedSpeaker.title || '',
            '', // bio - empty for now
            '', // voice - empty for now
            false, // Don't skip story addition
          );

          if (!speaker) {
            throw new Error(
              `Failed to create speaker ${identifiedSpeaker.name}`,
            );
          }

          // Add the new speaker to collections synchronously
          await runInAction(async () => {
            if (!editorStore.albumSpeakers.find(s => s.id === speaker.id)) {
              editorStore.albumSpeakers.push(speaker);
            }
            if (!editorStore.storySpeakers.find(s => s.id === speaker.id)) {
              editorStore.storySpeakers.push(speaker);
            }
          });
        }

        // Only map the primary speaker label
        const speakerId = speaker.id || speaker.datoSpeakerId;
        finalMappings[primarySpeakerLabel] = speakerId;
      }

      // Build the final mappings, including clearing secondary speakers
      const allMappings: Record<string, string> = {};

      // First, add all existing mappings
      Object.assign(allMappings, existingSpeakerMappings);

      // Then add new mappings
      Object.assign(allMappings, finalMappings);

      // Finally, explicitly clear mappings for all secondary speakers that were consolidated
      const consolidatedSecondaryLabels = new Set<string>();
      for (const [, { speakerLabels }] of speakersToConsolidate) {
        // Add all secondary labels (not the primary) to the set
        speakerLabels.slice(1).forEach(label => {
          consolidatedSecondaryLabels.add(label);
          // Clear the mapping by setting it to empty string
          allMappings[label] = '';
        });
      }

      // Now batch update all mappings at once
      if (Object.keys(allMappings).length > 0) {
        setApplyingProgress('Saving speaker mappings...');
        const success =
          await speakerService.updateMappingsTransactionally(allMappings);
        if (!success) {
          throw new Error('Failed to update speaker mappings');
        }
      }

      setApplyingProgress('Complete!');
      // Close the modal on success after a brief moment to show completion
      setTimeout(() => {
        onClose();
      }, 500);
    } catch (err) {
      console.error('Error applying speaker mappings:', err);
      setError('Failed to apply some speaker mappings. Please try again.');
    } finally {
      setIsApplying(false);
      setApplyingProgress('');
    }
  };

  const hasAnyMappings = Object.values(selectedMappings).some(
    mapping => mapping !== null,
  );

  return (
    <ModalOverlay onClick={onClose}>
      <ModalContent onClick={e => e.stopPropagation()}>
        <ModalHeader>
          <ModalTitle>Identify Speakers</ModalTitle>
          <CloseButton onClick={onClose}>×</CloseButton>
        </ModalHeader>

        <ModalBody>
          {!identifiedSpeakers.length && !isIdentifying && (
            <IntroSection>
              <IntroText>
                This feature analyzes your transcript to automatically identify
                speaker names and roles. It looks for introductions, greetings,
                and contextual mentions to determine who is speaking.
              </IntroText>
              <IdentifyButton
                onClick={handleIdentifySpeakers}
                disabled={isIdentifying}
              >
                {isIdentifying
                  ? 'Analyzing Transcript...'
                  : 'Analyze Transcript'}
              </IdentifyButton>
            </IntroSection>
          )}

          {(isIdentifying || isAnalyzingFrames) && (
            <LoadingSection>
              <LoadingSpinner />
              <LoadingText>
                {identificationProgress ||
                  (isAnalyzingFrames
                    ? 'Analyzing video frames for speaker information...'
                    : 'Analyzing transcript for speaker identities...')}
              </LoadingText>
            </LoadingSection>
          )}

          {identifiedSpeakers.length > 0 &&
            !isIdentifying &&
            !isAnalyzingFrames && (
              <>
                <ResultsHeader>
                  Found {identifiedSpeakers.length} speaker
                  {identifiedSpeakers.length !== 1 ? 's' : ''} in the
                  transcript. Review and confirm the mappings below:
                </ResultsHeader>

                <MappingsContainer>
                  {anonymousSpeakers.map(speaker => {
                    const currentMapping =
                      selectedMappings[speaker.speakerLabel];
                    const speakerColor =
                      editorStore.editor
                        ?.getSpeakerService()
                        ?.getSpeakerColor(speaker.speakerLabel) || '#A9A9A9';

                    return (
                      <MappingRow key={speaker.speakerLabel}>
                        <AnonymousSpeaker>
                          <SpeakerColorIndicator color={speakerColor} />
                          <SpeakerLabel>
                            Speaker{' '}
                            {parseInt(
                              speaker.speakerLabel.replace('spk_', ''),
                            ) + 1}
                          </SpeakerLabel>
                        </AnonymousSpeaker>

                        <Arrow>→</Arrow>

                        <MappingDropdown
                          speakerLabel={speaker.speakerLabel}
                          currentMapping={currentMapping}
                          identifiedSpeakers={identifiedSpeakers}
                          existingSpeakers={existingSpeakers}
                          onChange={handleMappingChange}
                          disabled={isApplying}
                        />
                      </MappingRow>
                    );
                  })}
                </MappingsContainer>

                {frameAnalysisResults.length > 0 && (
                  <FramePreviewSection>
                    <FramePreviewToggle>
                      <ToggleButton
                        onClick={() => setShowFramePreview(!showFramePreview)}
                      >
                        {showFramePreview ? '▼' : '▶'} Frame Analysis (
                        {
                          frameAnalysisResults.filter(r => r.hasLowerThird)
                            .length
                        }{' '}
                        detected)
                      </ToggleButton>
                    </FramePreviewToggle>

                    {showFramePreview && (
                      <FramePreviewGrid>
                        {frameAnalysisResults
                          .filter(r => r.hasLowerThird)
                          .map(result => (
                            <FrameCard
                              key={`${result.speakerLabel}-${result.frameNumber}`}
                            >
                              <FrameThumbnail
                                src={result.frameUrl}
                                alt={`Frame ${result.frameNumber}`}
                                loading="lazy"
                              />
                              <FrameOverlay>
                                <TimestampBadge>
                                  {formatTimestamp(result.timestamp)}
                                </TimestampBadge>
                                <SpeakerBadge>
                                  {getSpeakerNameFromLabel(result.speakerLabel)}
                                </SpeakerBadge>
                              </FrameOverlay>
                              <FrameDetails>
                                <DetectedName>
                                  {result.name || 'Unknown'}
                                </DetectedName>
                                {result.title && (
                                  <DetectedTitle>{result.title}</DetectedTitle>
                                )}
                                {result.company && (
                                  <DetectedCompany>
                                    {result.company}
                                  </DetectedCompany>
                                )}
                                <ConfidenceBadge confidence={result.confidence}>
                                  {result.confidence} confidence
                                </ConfidenceBadge>
                              </FrameDetails>
                            </FrameCard>
                          ))}
                      </FramePreviewGrid>
                    )}
                  </FramePreviewSection>
                )}
              </>
            )}

          {error && <ErrorMessage>{error}</ErrorMessage>}
        </ModalBody>

        {identifiedSpeakers.length > 0 && (
          <ModalFooter>
            <CancelButton onClick={onClose}>Cancel</CancelButton>
            <ApplyButton
              onClick={handleApplyMappings}
              disabled={!hasAnyMappings || isApplying}
            >
              {isApplying
                ? applyingProgress || 'Applying...'
                : 'Apply Mappings'}
            </ApplyButton>
          </ModalFooter>
        )}
      </ModalContent>
    </ModalOverlay>
  );
};

// Custom Dropdown Component
interface MappingDropdownProps {
  speakerLabel: string;
  currentMapping: IdentifiedSpeaker | Speaker | null;
  identifiedSpeakers: IdentifiedSpeaker[];
  existingSpeakers: Speaker[];
  onChange: (
    speakerLabel: string,
    mapping: IdentifiedSpeaker | Speaker | null,
  ) => void;
  disabled?: boolean;
}

const MappingDropdown: React.FC<MappingDropdownProps> = ({
  speakerLabel,
  currentMapping,
  identifiedSpeakers,
  existingSpeakers,
  onChange,
  disabled = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useOutsideAlerter(dropdownRef, () => {
    setIsOpen(false);
  });

  const handleSelect = (mapping: IdentifiedSpeaker | Speaker | null) => {
    onChange(speakerLabel, mapping);
    setIsOpen(false);
  };

  // Find which identified speaker corresponds to this speaker label
  const identifiedForThisSpeaker = identifiedSpeakers.find(
    id => id.speakerLabel === speakerLabel,
  );

  // Get potential matches for this speaker
  const potentialMatches = identifiedForThisSpeaker
    ? existingSpeakers.filter(existing => {
        const nameLower = identifiedForThisSpeaker.name.toLowerCase();
        const existingNameLower = existing.name.toLowerCase();

        // Check for name similarity
        if (
          existingNameLower.includes(nameLower) ||
          nameLower.includes(existingNameLower)
        )
          return true;

        // Check for last name match
        const identifiedLastName = nameLower.split(' ').pop() || '';
        const existingLastName = existingNameLower.split(' ').pop() || '';
        if (
          identifiedLastName.length > 2 &&
          identifiedLastName === existingLastName
        )
          return true;

        return false;
      })
    : [];

  const getDisplayText = (mapping: IdentifiedSpeaker | Speaker | null) => {
    if (!mapping) return 'No match found';

    const name = mapping.name;
    const title = 'title' in mapping ? mapping.title : mapping.title;
    const isExisting = 'datoSpeakerId' in mapping;

    return `${name}${title ? ` - ${title}` : ''}${isExisting ? ' (Existing)' : ' (New)'}`;
  };

  return (
    <DropdownContainer ref={dropdownRef}>
      <DropdownButton
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
      >
        <DropdownText>{getDisplayText(currentMapping)}</DropdownText>
        <DropdownArrow isOpen={isOpen}>
          <svg width="12" height="13" viewBox="0 0 12 13" fill="none">
            <path d="M2 4.33008L6 8.33008L10 4.33008" stroke="#F2D093" />
          </svg>
        </DropdownArrow>
      </DropdownButton>

      {isOpen && (
        <DropdownMenu>
          <DropdownItem onClick={() => handleSelect(null)}>
            No match found
          </DropdownItem>

          {/* Show potential existing speaker matches first */}
          {potentialMatches.length > 0 && (
            <>
              <DropdownSectionHeader>Existing Speakers</DropdownSectionHeader>
              {potentialMatches.map(speaker => (
                <DropdownItem
                  key={speaker.datoSpeakerId}
                  onClick={() => handleSelect(speaker)}
                  isSelected={
                    currentMapping &&
                    'datoSpeakerId' in currentMapping &&
                    currentMapping.datoSpeakerId === speaker.datoSpeakerId
                  }
                  isExisting
                >
                  {speaker.name} - {speaker.title || 'No title'}
                </DropdownItem>
              ))}
            </>
          )}

          {/* Show new speaker option if identified */}
          {identifiedForThisSpeaker && (
            <>
              <DropdownSectionHeader>Create New Speaker</DropdownSectionHeader>
              <DropdownItem
                onClick={() => handleSelect(identifiedForThisSpeaker)}
                isSelected={
                  currentMapping &&
                  'speakerLabel' in currentMapping &&
                  currentMapping.speakerLabel ===
                    identifiedForThisSpeaker.speakerLabel
                }
              >
                {identifiedForThisSpeaker.name} -{' '}
                {identifiedForThisSpeaker.title}
              </DropdownItem>
            </>
          )}

          {/* Show all other existing speakers */}
          {existingSpeakers.length > potentialMatches.length && (
            <>
              <DropdownSectionHeader>
                All Existing Speakers
              </DropdownSectionHeader>
              {existingSpeakers
                .filter(speaker => !potentialMatches.includes(speaker))
                .map(speaker => (
                  <DropdownItem
                    key={speaker.datoSpeakerId}
                    onClick={() => handleSelect(speaker)}
                    isSelected={
                      currentMapping &&
                      'datoSpeakerId' in currentMapping &&
                      currentMapping.datoSpeakerId === speaker.datoSpeakerId
                    }
                    isExisting
                  >
                    {speaker.name} - {speaker.title || 'No title'}
                  </DropdownItem>
                ))}
            </>
          )}
        </DropdownMenu>
      )}
    </DropdownContainer>
  );
};

// Styled Components
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background-color: #1a1e23;
  border-radius: 8px;
  width: 90%;
  max-width: 600px;
  max-height: 80vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.5);
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px;
  border-bottom: 1px solid #2a2f36;
`;

const ModalTitle = styled.h2`
  margin: 0;
  font-size: 20px;
  font-weight: 600;
  color: #f2d093;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 24px;
  color: #a0a6ae;
  cursor: pointer;
  padding: 0;
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  transition: all 0.2s;

  &:hover {
    background-color: #2a2f36;
    color: #ffffff;
  }
`;

const ModalBody = styled.div`
  padding: 20px;
  flex: 1;
  overflow-y: auto;
`;

const IntroSection = styled.div`
  text-align: center;
  padding: 40px 20px;
`;

const IntroText = styled.p`
  color: #a0a6ae;
  font-size: 14px;
  line-height: 1.6;
  margin-bottom: 24px;
  max-width: 400px;
  margin-left: auto;
  margin-right: auto;
`;

const IdentifyButton = styled.button`
  background-color: #f2d093;
  color: #1a1a2a;
  border: none;
  padding: 12px 24px;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;

  &:hover:not(:disabled) {
    background-color: #f5d9a3;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const LoadingSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
`;

const LoadingSpinner = styled.div`
  width: 40px;
  height: 40px;
  border: 3px solid #2a2f36;
  border-top-color: #f2d093;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 16px;

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const LoadingText = styled.p`
  color: #a0a6ae;
  font-size: 14px;
`;

const ResultsHeader = styled.p`
  color: #a0a6ae;
  font-size: 14px;
  margin-bottom: 20px;
`;

const MappingsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const MappingRow = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 12px;
  background-color: #0d1015;
  border-radius: 4px;
`;

const AnonymousSpeaker = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 0 0 140px;
`;

const SpeakerColorIndicator = styled.div<{ color: string }>`
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background-color: ${props => props.color};
`;

const SpeakerLabel = styled.span`
  color: #ffffff;
  font-size: 14px;
  font-weight: 500;
`;

const Arrow = styled.span`
  color: #484c54;
  font-size: 18px;
`;

// Dropdown styled components
const DropdownContainer = styled.div`
  position: relative;
  flex: 1;
  max-width: 340px;
`;

const DropdownButton = styled.div<{ disabled?: boolean }>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  height: 40px;
  padding: 8px 16px;
  background-color: #03041a;
  border: 1px solid #484848;
  border-radius: 8px;
  color: #ffffff;
  font-size: 14px;
  font-family: 'Inter', sans-serif;
  cursor: ${props => (props.disabled ? 'not-allowed' : 'pointer')};
  opacity: ${props => (props.disabled ? 0.6 : 1)};
  transition: all 0.2s ease;

  &:hover {
    border-color: ${props => (props.disabled ? '#484848' : '#f2d093')};
  }
`;

const DropdownText = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const DropdownArrow = styled.div<{ isOpen: boolean }>`
  display: flex;
  align-items: center;
  transition: transform 0.2s ease;
  transform: ${props => (props.isOpen ? 'rotate(180deg)' : 'rotate(0deg)')};
`;

const DropdownMenu = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  margin-top: 4px;
  background-color: #03041a;
  border: 1px solid #484848;
  border-radius: 8px;
  box-shadow: 8px 16px 8px 0px rgba(0, 0, 0, 0.4);
  max-height: 200px;
  overflow-y: auto;
  z-index: 10;
  padding: 5px 10px;

  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-thumb {
    background-color: #35393a;
    border-radius: 4px;
  }

  &::-webkit-scrollbar-track {
    background-color: transparent;
  }
`;

const DropdownItem = styled.div<{ isSelected?: boolean; isExisting?: boolean }>`
  padding: 12px;
  font-size: 14px;
  font-family: 'Inter', sans-serif;
  color: ${props => (props.isSelected ? '#f2d093' : '#ffffff')};
  cursor: pointer;
  transition: all 0.2s ease;
  position: relative;
  padding-left: ${props => (props.isExisting ? '24px' : '12px')};

  ${props =>
    props.isExisting &&
    `
    &::before {
      content: '✓';
      position: absolute;
      left: 8px;
      color: #17c964;
      font-size: 12px;
    }
  `}

  &:not(:last-child) {
    border-bottom: 1px solid #484848;
  }

  &:hover {
    color: #f2d093;
    background-color: rgba(242, 208, 147, 0.1);
  }
`;

const DropdownSectionHeader = styled.div`
  padding: 8px 12px;
  font-size: 12px;
  font-weight: 600;
  color: #a0a6ae;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  border-bottom: 1px solid #484848;
  background-color: rgba(48, 52, 59, 0.5);
`;

const ErrorMessage = styled.div`
  color: #ff4545;
  font-size: 14px;
  margin-top: 16px;
  padding: 12px;
  background-color: rgba(255, 69, 69, 0.1);
  border-radius: 4px;
`;

const ModalFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 20px;
  border-top: 1px solid #2a2f36;
`;

const CancelButton = styled.button`
  background: transparent;
  color: #a0a6ae;
  border: 1px solid #2a2f36;
  padding: 8px 20px;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    border-color: #484c54;
    color: #ffffff;
  }
`;

const ApplyButton = styled.button`
  background-color: #f2d093;
  color: #1a1a2a;
  border: none;
  padding: 8px 20px;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;

  &:hover:not(:disabled) {
    background-color: #f5d9a3;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

// Frame preview styled components
const FramePreviewSection = styled.div`
  margin-top: 20px;
  border-top: 1px solid #2a2f36;
  padding-top: 20px;
`;

const FramePreviewToggle = styled.div`
  margin-bottom: 16px;
`;

const ToggleButton = styled.button`
  background: none;
  border: none;
  color: #f2d093;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0;

  &:hover {
    color: #f5d9a3;
  }
`;

const FramePreviewGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  gap: 16px;
  max-height: 400px;
  overflow-y: auto;

  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-thumb {
    background-color: #35393a;
    border-radius: 4px;
  }

  &::-webkit-scrollbar-track {
    background-color: transparent;
  }
`;

const FrameCard = styled.div`
  background-color: #0d1015;
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid #2a2f36;
  transition: border-color 0.2s;
  position: relative;

  &:hover {
    border-color: #484c54;
  }
`;

const FrameThumbnail = styled.img`
  width: 100%;
  height: 140px;
  object-fit: cover;
`;

const FrameOverlay = styled.div`
  position: absolute;
  top: 8px;
  left: 8px;
  right: 8px;
  display: flex;
  justify-content: space-between;
  pointer-events: none;
`;

const TimestampBadge = styled.span`
  background-color: rgba(0, 0, 0, 0.7);
  color: #ffffff;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
`;

const SpeakerBadge = styled.span`
  background-color: #03041a;
  color: #ffffff;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
`;

const FrameDetails = styled.div`
  padding: 12px;
`;

const DetectedName = styled.div`
  color: #ffffff;
  font-size: 14px;
  font-weight: 600;
  margin-bottom: 4px;
`;

const DetectedTitle = styled.div`
  color: #a0a6ae;
  font-size: 12px;
  margin-bottom: 2px;
`;

const DetectedCompany = styled.div`
  color: #a0a6ae;
  font-size: 12px;
  margin-bottom: 8px;
`;

const ConfidenceBadge = styled.div<{ confidence: string }>`
  display: inline-block;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 500;
  background-color: ${props =>
    props.confidence === 'high'
      ? 'rgba(23, 201, 100, 0.2)'
      : props.confidence === 'medium'
        ? 'rgba(242, 208, 147, 0.2)'
        : 'rgba(255, 69, 69, 0.2)'};
  color: ${props =>
    props.confidence === 'high'
      ? '#17c964'
      : props.confidence === 'medium'
        ? '#f2d093'
        : '#ff4545'};
`;

export default SpeakerIdentificationModal;
